<template>
  <div class="participant-monitoring">
    <!-- 필터 섹션 -->
    <div class="filter-section mb-4">
      <!-- 필터 헤더 (항상 보임) -->
      <div class="filter-header">
        <a-row :gutter="16" align="middle" justify="space-between">
          <a-col>
            <span class="filter-title">
              <i class="fa fa-filter"></i>
              필터 옵션
              <a-badge v-if="activeFilterCount > 0" :count="activeFilterCount" :number-style="{ backgroundColor: '#52c41a', fontSize: '12px' }" style="margin-left: 8px;" />
            </span>
          </a-col>
          <a-col>
            <a-button type="text" @click="isFilterCollapsed = !isFilterCollapsed" class="collapse-button">
              <template #icon>
                <i :class="isFilterCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
              </template>
              {{ isFilterCollapsed ? '펼치기' : '접기' }}
            </a-button>
          </a-col>
        </a-row>
      </div>

      <!-- 접을 수 있는 필터 콘텐츠 -->
      <div v-show="!isFilterCollapsed" class="filter-content">
        <!-- 첫 번째 줄: 이벤트 필터 -->
        <a-row :gutter="16" align="middle" class="filter-row mt-3">
        <a-col :span="6" v-if="!hideEventSelect">
          <label class="filter-label">
            이벤트
            <span v-if="selectedEvent" style="margin-left: 8px;">
              <a-typography-text
                :copyable="{ text: selectedEvent }"
                style="color: #666; font-size: 12px;"
              >
                (ID: {{ selectedEvent }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="selectedEvent"
            placeholder="이벤트를 선택해주세요."
            style="width: 100%"
            :loading="eventsLoading"
            @change="handleEventChange"
            allow-clear
          >
            <a-select-option v-for="event in eventList" :key="event._id" :value="event._id">
              {{ event.eventTitle }} ({{ getEventStatusText(event.eventStatus) }})
            </a-select-option>
          </a-select>
        </a-col>
        <a-col :span="4">
          <label class="filter-label">
            유입 경로
            <a-tooltip>
              <template #title>
                <div>
                  인증서 수신자: referrerId와 referrerId2 모두 없음<br/>
                  환자의 친구: referrerId가 있고 referrerId2 없음<br/>
                  친구의 친구: referrerId와 referrerId2 모두 있음
                </div>
              </template>
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedReferralSource"
            placeholder="유입 경로 선택"
            style="width: 100%"
            @change="handleReferralSourceChange"
            allow-clear
          >
            <a-select-option value="certificate">인증서 수신자</a-select-option>
            <a-select-option value="patient_friend">환자의 친구</a-select-option>
            <a-select-option value="friend_of_friend">친구의 친구</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="4">
          <label class="filter-label">
            과거 이벤트 참여 이력
            <a-tooltip title="이전에 다른 이벤트에 참여한 이력 여부">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedEventMetadataFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleEventMetadataFilterChange"
            allow-clear
          >
            <a-select-option value="exists">참여 이력 있음</a-select-option>
            <a-select-option value="notExists">참여 이력 없음(신규)</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="10"></a-col>
      </a-row>
      
      <!-- 두 번째 줄: 쿠폰, 리뷰 및 마케팅 필터 추가 -->
      <a-row :gutter="16" align="middle" class="filter-row mt-3">
        <a-col :span="4">
          <label class="filter-label">
            쿠폰 발급 권한
            <a-tooltip title="couponInfo 객체가 있는 경우">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedCouponInfo"
            placeholder="전체"
            style="width: 100%"
            @change="handleCouponInfoChange"
            allow-clear
          >
            <a-select-option value="exists">권한 있음</a-select-option>
            <a-select-option value="notExists">권한 없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="3">
          <label class="filter-label">
            쿠폰 수령 여부
            <a-tooltip title="couponInfo.couponNumber 가 있는 경우">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedCouponNumber"
            placeholder="전체"
            style="width: 100%"
            @change="handleCouponNumberChange"
            :disabled="selectedCouponInfo !== 'exists'"
            allow-clear
          >
            <a-select-option value="exists">수령함</a-select-option>
            <a-select-option value="notExists">미수령</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="3">
          <label class="filter-label">
            최소 쿠폰 수
            <a-tooltip title="couponInfo 배열의 갯수">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-number
            v-model:value="minCouponCount"
            :min="1"
            placeholder="쿠폰 수량"
            style="width: 100%"
            @change="handleMinCouponCountChange"
          />
        </a-col>
        <a-col :span="4">
          <label class="filter-label">
            리뷰 유무
            <a-tooltip title="리뷰 배열 자체의 존재 여부를 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedReviewFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleReviewFilterChange"
            allow-clear
          >
            <a-select-option value="exists">있음</a-select-option>
            <a-select-option value="notExists">없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="4">
          <label class="filter-label">
            리뷰 내용 유무
            <a-tooltip title="리뷰 내용(content)의 존재 여부를 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedReviewContentFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleReviewContentFilterChange"
            :disabled="selectedReviewFilter !== 'exists'"
            allow-clear
          >
            <a-select-option value="exists">내용 있음</a-select-option>
            <a-select-option value="notExists">내용 없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="4">
          <label class="filter-label">
            마케팅 수신 동의
            <a-tooltip title="마케팅 수신 동의 여부로 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedMarketingConsent"
            placeholder="전체"
            style="width: 100%"
            @change="handleMarketingConsentChange"
            allow-clear
          >
            <a-select-option value="agreed">동의</a-select-option>
            <a-select-option value="revoked">동의안함</a-select-option>
          </a-select>
        </a-col>
      </a-row>
      
      <!-- 세 번째 줄: 액티비티 유형 -->
      <a-row :gutter="16" align="middle" class="filter-row mt-3" v-if="availableActivityTypes.length > 0">
        <a-col :span="24">
          <label class="filter-label">
            액티비티 유형
            <a-tooltip title="필터형식: 이벤트활동 유형 이름(이벤트 활동 유형의 집계 수량)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <div class="activity-checkbox-group">
            <a-checkbox-group 
              v-model:value="selectedActivityTypes" 
              @change="handleActivityTypeChange"
              style="width: 100%"
            >
              <a-row :gutter="[24, 16]">
                <a-col 
                  v-for="activityType in availableActivityTypes" 
                  :key="activityType.type"
                  :span="6"
                >
                  <a-checkbox :value="activityType.type">
                    <span class="activity-label">{{ activityType.label }}</span>
                  </a-checkbox>
                </a-col>
              </a-row>
            </a-checkbox-group>
          </div>
        </a-col>
      </a-row>
      
      <!-- 세 번째 줄: 날짜 필터 -->
      <a-row :gutter="16" align="middle" class="filter-row mt-3">
        <a-col :span="5">
          <label class="filter-label">
            날짜 필터 유형
            <a-tooltip title="날짜 필터 기준 선택 (한 번에 하나만 적용 가능)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedDateType"
            placeholder="이벤트 참여시간"
            style="width: 100%"
            @change="handleDateTypeChange"
            allow-clear
          >
            <a-select-option value="participatedAt">이벤트 참여시간</a-select-option>
            <a-select-option value="surgeryDate">수술일자</a-select-option>
            <a-select-option value="couponCreatedAt">쿠폰 정보 생성시간</a-select-option>
            <a-select-option value="couponIssuedAt">쿠폰 발급시간</a-select-option>
            <a-select-option value="couponExpiryDate">쿠폰 만료일</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="7">
          <label class="filter-label">
            날짜 범위
            <a-tooltip title="선택한 날짜 필터 유형에 대한 기간 설정">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-range-picker
            v-model:value="selectedDateRange"
            style="width: 100%"
            :placeholder="['시작일', '종료일']"
            format="YYYY-MM-DD"
            @change="handleDateRangeChange"
            :disabled="!selectedDateType"
          />
        </a-col>
        <a-col :span="12"></a-col>
      </a-row>

      <!-- 네 번째 줄: 검색 및 액션 버튼 -->
      <a-row :gutter="16" align="middle" class="filter-row mt-3">
        <a-col :span="6">
          <label class="filter-label">
            검색
            <a-tooltip title="이름 또는 전화번호로 검색">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-search
            v-model:value="searchKeyword"
            placeholder="이름, 전화번호로 검색"
            style="width: 100%"
            @search="handleSearch"
          />
        </a-col>
        <a-col :span="18" style="text-align: right;">
          <a-space>
            <a-badge :count="activeFilterCount" :number-style="{ backgroundColor: '#52c41a' }" v-if="activeFilterCount > 0">
              <a-button type="text" disabled>
                {{ activeFilterCount }}개 필터 적용중
              </a-button>
            </a-badge>
            <a-tooltip title="모든 필터 초기화">
              <a-button @click="resetAllFilters">
                <template #icon><i class="fa fa-sync-alt"></i></template>
                필터 초기화
              </a-button>
            </a-tooltip>
            <a-button @click="handleRefresh">
              <template #icon><i class="fa fa-refresh"></i></template>
              새로고침
            </a-button>
          </a-space>
        </a-col>
      </a-row>
      </div> <!-- filter-content 닫기 -->
    </div>

    <!-- 참여자 테이블 -->
    <div class="table-section">
      <div class="table-header mb-3">
        <a-row align="middle" justify="space-between">
          <a-col :span="18">
            <a-space>
              <span v-if="selectedRows.length > 0">
                <a-tag color="blue">{{ selectedRows.length }}명 선택됨</a-tag>
              </span>
              <!-- 메시지 발송 버튼 주석처리 -->
              <!-- <a-button
                v-if="selectedRows.length > 0"
                type="primary"
                size="small"
                @click="handleSendMessage"
                class="yellow-button"
              >
                선택한 대상에게 메시지 발송
              </a-button> -->
              <a-button
                v-if="selectedRows.length > 0"
                type="primary"
                size="small"
                @click="handleSendWinnerNotification"
                class="yellow-button"
              >
                <template #icon><i class="fa fa-trophy"></i></template>
                당첨알림 발송
              </a-button>
            </a-space>
          </a-col>
          <a-col :span="6" style="text-align: right;">
            <!-- 버튼 제거됨 -->
          </a-col>
        </a-row>
      </div>

      <a-table
        :columns="columns"
        :data-source="filteredData"
        :row-selection="{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectChange,
        }"
        :pagination="pagination"
        @change="handleTableChange"
        :loading="loading"
        size="small"
        :scroll="{ x: 1400 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'certificates'">
            <div v-if="record.certificates && record.certificates.length > 0" class="certificates-cell">
              <div style="position: relative; display: inline-block;">
                <i class="fa fa-certificate" style="font-size: 18px; color: #52c41a;"></i>
                <span class="custom-badge-count">{{ record.certificates.length }}</span>
              </div>
            </div>
            <span v-else style="color: #ccc;">-</span>
          </template>

          <template v-if="column.key === 'customerName'">
            <span>{{ record.customerName || '-' }}</span>
          </template>
          
          <template v-if="column.key === 'customerInfoName'">
            <span>{{ record.customerInfo?.name || '-' }}</span>
          </template>
          
          <template v-if="column.key === 'customerMobile'">
            <span>{{ record.customerMobile || '-' }}</span>
          </template>
          
          <template v-if="column.key === 'customerInfoMobile'">
            <span>{{ record.customerInfo?.mobile || '-' }}</span>
          </template>

          <template v-if="column.key === 'customerType'">
            <a-tag v-if="record.customerType" :color="getCustomerTypeColor(record.customerType)">
              {{ getCustomerTypeLabel(record.customerType) }}
            </a-tag>
            <span v-else>-</span>
          </template>

          <template v-if="column.key === 'referralSource'">
            <a-tooltip v-if="getReferralSource(record) === '환자의 친구'">
              <template #title>
                referrerId: {{ record.referrerId || '-' }}
              </template>
              <a-tag :color="getReferralSourceColor(record)">
                {{ getReferralSource(record) }}
              </a-tag>
            </a-tooltip>
            <a-tooltip v-else-if="getReferralSource(record) === '친구의 친구'">
              <template #title>
                <div>
                  referrerId: {{ record.referrerId || '-' }}<br/>
                  referrerId2: {{ record.referrerId2 || '-' }}
                </div>
              </template>
              <a-tag :color="getReferralSourceColor(record)">
                {{ getReferralSource(record) }}
              </a-tag>
            </a-tooltip>
            <a-tag v-else :color="getReferralSourceColor(record)">
              {{ getReferralSource(record) }}
            </a-tag>
          </template>
          
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          
          <template v-if="column.key === 'marketingConsent'">
            <a-tag v-if="record.marketingConsent === 'agreed'" color="green">
              동의
            </a-tag>
            <a-tag v-else-if="record.marketingConsent === 'revoked'" color="red">
              미동의
            </a-tag>
            <span v-else>-</span>
          </template>
          
          <template v-if="column.key === 'progress'">
            <div class="progress-indicators">
              <a-tooltip title="이벤트 참여">
                <span class="indicator" :class="{ active: hasParticipated(record) }">
                  <i class="fa fa-user"></i>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.reviewWritten }"></span>
              <a-tooltip :title="`리뷰 작성${record.reviewCount > 0 ? ` (${record.reviewCount}개)` : ''}`">
                <span class="indicator" :class="{ active: record.reviewWritten }">
                  <i class="fa fa-star"></i>
                  <span v-if="record.reviewCount > 1" class="share-count">{{ record.reviewCount }}</span>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.sharedAt }"></span>
              <a-tooltip :title="`공유하기${record.shareCount > 0 ? ` (${record.shareCount}회)` : ''}`">
                <span class="indicator" :class="{ active: record.sharedAt }">
                  <i class="fa fa-share-alt"></i>
                  <span v-if="record.shareCount > 1" class="share-count">{{ record.shareCount }}</span>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.couponReceived }"></span>
              <a-tooltip :title="`쿠폰 수령${record.couponInfo && record.couponInfo.length > 0 ? ` (${record.couponInfo.length}개)` : ''}`">
                <span class="indicator" :class="{ active: record.couponReceived }">
                  <i class="fa fa-ticket"></i>
                  <span v-if="record.couponInfo && record.couponInfo.length > 1" class="share-count">{{ record.couponInfo.length }}</span>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.loggedIn }"></span>
              <a-tooltip title="로그인">
                <span class="indicator" :class="{ active: record.loggedIn }">
                  <i class="fa fa-sign-in"></i>
                </span>
              </a-tooltip>
            </div>
          </template>
          
          <template v-if="column.key === 'eventMetadata'">
            <div v-if="record.eventMetadata && Array.isArray(record.eventMetadata) && record.eventMetadata.length > 0">
              <div style="font-size: 12px; line-height: 1.4;">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                  <div style="font-weight: 600; color: #1890ff;">
                    총 {{ record.eventMetadata.length }}개 이벤트 참여
                  </div>
                  <a-button
                    v-if="record.eventMetadata.length > 2"
                    size="small"
                    type="link"
                    @click="toggleEventExpand(record._id)"
                    style="padding: 0; height: auto; font-size: 11px;"
                  >
                    {{ expandedEvents[record._id] ? '접기' : '펼쳐보기' }}
                    <i :class="expandedEvents[record._id] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="margin-left: 4px;"></i>
                  </a-button>
                </div>
                <div
                  v-for="(event, index) in getDisplayedEvents(record)"
                  :key="index"
                  style="margin-bottom: 6px; padding: 4px; background: #f5f5f5; border-radius: 4px; cursor: pointer; transition: background 0.3s;"
                  @click="handleSingleEventClick(record, event)"
                  @mouseenter="$event.currentTarget.style.background = '#e6f7ff'"
                  @mouseleave="$event.currentTarget.style.background = '#f5f5f5'"
                >
                  <div style="font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ event.eventTitle || '제목 없음' }}
                  </div>
                  <div style="display: flex; gap: 8px; margin-top: 2px; flex-wrap: wrap;">
                    <span style="color: #666;">{{ formatDate(event.participatedAt).split(' ')[0] }}</span>
                    <span v-if="event.reviewWritten" style="color: #52c41a;">✅리뷰</span>
                    <span v-else style="color: #999;">❌리뷰</span>
                    <span v-if="event.surveyCompleted" style="color: #52c41a;">✅설문</span>
                    <span v-else style="color: #999;">❌설문</span>
                    <span v-if="event.coupons && event.coupons.length > 0">
                      <!-- 마케팅 수신 동의가 있으면 모두 수령으로 표시 -->
                      <span v-if="record.marketingConsent === 'agreed'" style="color: #52c41a;">
                        🎫쿠폰{{ event.coupons.length }}개(수령)
                      </span>
                      <!-- 마케팅 수신 동의가 없으면 기존 로직 사용 -->
                      <span v-else-if="event.coupons.some(c => c.status === 'COUPON_ISSUED')" style="color: #52c41a;">
                        🎫쿠폰{{ event.coupons.filter(c => c.status === 'COUPON_ISSUED').length }}개(수령)
                      </span>
                      <span v-else style="color: #faad14;">
                        🎫쿠폰{{ event.coupons.length }}개(미수령)
                      </span>
                    </span>
                    <span v-else style="color: #999;">🎫쿠폰0개</span>
                  </div>
                </div>
              </div>
            </div>
            <span v-else style="color: #999;">-</span>
          </template>
          
          <template v-if="column.key === 'lastActivity'">
            {{ formatDate(record.lastActivity) }}
          </template>
          
          <template v-if="column.key === 'copyIds'">
            <a-space>
              <a-tooltip title="고객 아이디 복사">
                <a-button
                  size="small"
                  @click="copyCustomerId(record)"
                  :disabled="!record.customerId"
                >
                  <i class="fa fa-user"></i> 고객
                </a-button>
              </a-tooltip>
              <a-tooltip title="이벤트 참여자 아이디 복사">
                <a-button
                  size="small"
                  @click="copyEventAttendeesId(record)"
                >
                  <i class="fa fa-calendar"></i> 참여자
                </a-button>
              </a-tooltip>
            </a-space>
          </template>

          <template v-if="column.key === 'generateLink'">
            <a-space>
              <a-tooltip title="환자 링크 복사">
                <a-button
                  size="small"
                  @click="copyPatientLink(record)"
                >
                  <i class="fa fa-user-injured"></i> 환자
                </a-button>
              </a-tooltip>
              <a-tooltip title="친구 링크 복사">
                <a-button
                  size="small"
                  @click="copyFriendLink(record)"
                >
                  <i class="fa fa-users"></i> 친구
                </a-button>
              </a-tooltip>
            </a-space>
          </template>

          <template v-if="column.key === 'actions'">
            <a-dropdown>
              <a-button size="small">
                액션 <i class="fa fa-caret-down"></i>
              </a-button>
              <template #overlay>
                <a-menu>
                  <!-- 메시지 발송 메뉴 주석처리 -->
                  <!-- <a-menu-item @click="sendIndividualMessage(record)">
                    <i class="fa fa-envelope"></i> 메시지 발송
                  </a-menu-item> -->
                  <a-menu-item @click="viewDetail(record)">
                    <i class="fa fa-eye"></i> 상세 보기
                  </a-menu-item>
                  <!-- <a-menu-divider /> -->
                  <!-- 참여정보 초기화 메뉴 주석처리 -->
                  <!-- <a-menu-item @click="resetParticipationInfo(record)">
                    <i class="fa fa-refresh"></i> 참여정보 초기화
                  </a-menu-item> -->
                </a-menu>
              </template>
            </a-dropdown>
          </template>
        </template>
      </a-table>
    </div>
    
    <!-- 참여자 상세 정보 Drawer -->
    <ParticipantDetailDrawer
      :visible="detailDrawerVisible"
      :participant="selectedParticipant"
      @close="detailDrawerVisible = false"
      @coupon-deleted="handleCouponDeleted"
      @coupon-resent="handleCouponResent"
    />
    
    <!-- 메시지 발송 Drawer -->
    <MessageSendDrawer
      :visible="messageDrawerVisible"
      :recipients="messageRecipients"
      :event-id="selectedEvent"
      @close="messageDrawerVisible = false"
      @send="handleMessageSent"
    />
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect, onMounted, onUnmounted, inject, getCurrentInstance, h } from 'vue';
import { useRouter } from 'vue-router';
import { message, Tooltip, Typography, Modal } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';
import { formatAndMaskPhone, maskName } from '@/utils/phoneFormatter';
import eventsApis from '../api.js';
import dayjs from 'dayjs';
import ParticipantDetailDrawer from './ParticipantDetailDrawer.vue';
import MessageSendDrawer from './MessageSendDrawer.vue';

const { Text: TypographyText } = Typography;

export default {
  name: 'ParticipantMonitoring',
  components: {
    ParticipantDetailDrawer,
    MessageSendDrawer,
    ATypographyText: TypographyText
  },
  props: {
    participants: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    eventList: {
      type: Array,
      default: () => []
    },
    selectedEventId: {
      type: String,
      default: 'all'
    },
    hideEventSelect: {
      type: Boolean,
      default: false
    }
  },
  emits: ['filter-change', 'event-change', 'send-message'],
  setup(props, { emit }) {
    // Router 인스턴스
    const router = useRouter();

    // 필터 영역 접기/펼치기 상태
    const isFilterCollapsed = ref(false);

    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiMethods = null;

    if (instance && instance.proxy) {
      apiMethods = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
    } else {
      console.error('Current instance not available, API initialization failed');
      message.error('시스템 초기화 오류가 발생했습니다.');
      return {};
    }
    
    // API functions 가져오기
    const getEventsWithStatus = apiMethods.getEventsWithStatus;
    const getEventAttendeesWithActivities = apiMethods.getEventAttendeesWithActivities;
    const startCertificateSync = apiMethods.startCertificateSync;
    const getCertificateSyncStatus = apiMethods.getCertificateSyncStatus;
    
    const searchKeyword = ref('');
    const selectedRowKeys = ref([]);
    const selectedRows = ref([]);
    const selectedReferralSource = ref(null);
    const selectedCouponInfo = ref(null);
    const selectedCouponNumber = ref(null);
    const selectedDateType = ref('participatedAt'); // 기본값: 이벤트 참여시간
    const minCouponCount = ref(null); // 최소 쿠폰 수 필터
    const selectedDateRange = ref(null);
    const selectedReviewFilter = ref(null); // 리뷰 필터 상태

    // 인증서 동기화 관련 상태
    const syncStatus = ref('idle'); // idle, processing, completed, failed
    const syncProgress = ref({
      totalAttendees: 0,
      processedCount: 0,
      skippedCount: 0,
      progressPercentage: 0,
      currentPhase: null
    });
    const pollingInterval = ref(null);
    const syncJobId = ref(null);
    const pollingCount = ref(0);
    const pollingStartTime = ref(null);
    const MAX_POLLING_COUNT = 120; // 최대 120회 시도
    const MAX_POLLING_TIME = 10 * 60 * 1000; // 10분 (밀리초)
    const syncElapsedTime = ref('');
    const selectedReviewContentFilter = ref(null); // 리뷰 컨텐츠 필터 상태
    const selectedMarketingConsent = ref(null); // 마케팅 수신 동의 필터
    const selectedEventMetadataFilter = ref(null); // 과거 이벤트 참여 이력 필터

    // 세그먼트 동기화 관련 상태
    const segmentSyncStatus = ref('idle'); // idle, processing, completed, failed
    const segmentSyncProgress = ref({
      remainingCount: 0,
      syncedCount: 0,
      totalCount: 0,
      percentage: 0
    });
    const segmentPollingInterval = ref(null);
    const segmentPollingCount = ref(0);
    const segmentPollingStartTime = ref(null);
    const segmentElapsedTime = ref('');

    // 펼쳐보기 상태 관리
    const expandedEvents = ref({});

    // Drawer 상태
    const detailDrawerVisible = ref(false);
    const selectedParticipant = ref({});
    const messageDrawerVisible = ref(false);
    const messageRecipients = ref([]);
    
    // 새로운 필터 상태
    const selectedEvent = ref(props.selectedEventId);
    const selectedActivityTypes = ref([]);
    
    // 페이지네이션 상태
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `총 ${total}명`,
      pageSizeOptions: ['10', '20', '50', '100', '150', '200']
    });
    
    // API 로딩 상태
    const eventsLoading = ref(false);
    
    // API 데이터 상태
    const eventList = ref([]);
    const availableActivityTypes = ref([]);
    const participantData = ref([]);
    
    // props 변경 감지하여 동기화 및 자동 조회
    watchEffect(() => {
      if (props.selectedEventId && props.selectedEventId !== selectedEvent.value) {
        selectedEvent.value = props.selectedEventId;
        // 이벤트가 변경되면 자동으로 참여자 로드
        if (props.selectedEventId !== 'all' && props.selectedEventId !== '') {
          console.log('Event changed via props, loading participants:', props.selectedEventId);
          pagination.value.current = 1; // 페이지 리셋
          loadParticipants();
        }
      }
    });

    // 컬럼 정의
    const columns = [
      {
        title: '순서',
        key: 'index',
        width: 60,
        fixed: 'left',
        customRender: ({ index }) => {
          const currentPage = pagination.value.current || 1;
          const pageSize = pagination.value.pageSize || 10;
          return (currentPage - 1) * pageSize + index + 1;
        }
      },
      {
        title: '인증서보유',
        key: 'certificates',
        width: 90,
        fixed: 'left',
        align: 'center'
      },
      {
        title: () => h('span', [
          '참여자명',
          h(Tooltip, { title: 'eventattendees 컬렉션 기준 데이터' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: 'customerName',
        key: 'customerName',
        width: 100,
        fixed: 'left'
      },
      {
        title: () => h('span', [
          '고객명',
          h(Tooltip, { title: 'customers 컬렉션 기준 데이터' }, () => 
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: ['customerInfo', 'name'],
        key: 'customerInfoName',
        width: 100
      },
      {
        title: () => h('span', [
          '참여자 연락처',
          h(Tooltip, { title: 'eventattendees 컬렉션 기준 데이터' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: 'customerMobile',
        key: 'customerMobile',
        width: 130
      },
      {
        title: () => h('span', [
          '고객 연락처',
          h(Tooltip, { title: 'customers 컬렉션 기준 데이터' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: ['customerInfo', 'mobile'],
        key: 'customerInfoMobile',
        width: 130
      },
      {
        title: () => h('span', [
          '고객분류',
          h(Tooltip, {
            title: h('div', { style: 'max-width: 400px; background: #fff; padding: 12px; border-radius: 4px;' }, [
              h('div', { style: 'font-weight: bold; margin-bottom: 8px; color: #333;' }, '📊 4가지 고객 분류'),
              // THERMAGE 관련 항목 주석 처리 (추후 노출 예정)
              // h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
              //   h('b', { style: 'color: #9b59b6;' }, '1️⃣ THERMAGE_WITH_ZEISS'),
              //   h('span', { style: 'color: #666;' }, ': 써마지 이벤트 참여 + 자이스 렌즈 삽입술 인증서 보유 (프리미엄 고객군)')
              // ]),
              // h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
              //   h('b', { style: 'color: #e67e22;' }, '2️⃣ THERMAGE_ONLY'),
              //   h('span', { style: 'color: #666;' }, ': 써마지 이벤트만 참여, 자이스 인증서 없음 (피부과 시술 관심 고객)')
              // ]),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #3498db;' }, '1️⃣ EXISTING_CERTIFICATE'),
                h('span', { style: 'color: #666;' }, ': 이전에 수술 인증서를 발급받은 환자 (기존 고객)')
              ]),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #27ae60;' }, '2️⃣ NEW_CERTIFICATE'),
                h('span', { style: 'color: #666;' }, ': 이번 이벤트에서 처음 인증서 발급 (신규 고객)')
              ]),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #95a5a6;' }, '3️⃣ NON_CERTIFICATE'),
                h('span', { style: 'color: #666;' }, ': 이벤트 참여했지만 인증서 없음 (잠재 고객)')
              ]),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #e74c3c;' }, '4️⃣ NOT_PARTICIPATED'),
                h('span', { style: 'color: #666;' }, ': 이벤트 자체에 참여하지 않음 (비활성 고객)')
              ])
            ])
          }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: 'customerType',
        key: 'customerType',
        width: 120
      },
      {
        title: () => h('span', [
          '유입 경로',
          h(Tooltip, {
            title: h('div', { style: 'max-width: 400px; background: #fff; padding: 12px; border-radius: 4px;' }, [
              h('div', { style: 'font-weight: bold; margin-bottom: 8px; color: #333;' }, '🛤️ 3가지 유입 경로'),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #8b5cf6;' }, '1️⃣ 인증서 수신자'),
                h('span', { style: 'color: #666;' }, ': 직접 인증서를 받은 환자 (referrerId와 referrerId2 모두 없음)')
              ]),
              h('div', { style: 'margin-bottom: 6px; color: #555;' }, [
                h('b', { style: 'color: #10b981;' }, '2️⃣ 환자의 친구'),
                h('span', { style: 'color: #666;' }, ': 인증서 수신자의 추천으로 참여 (referrerId 있음, referrerId2 없음)')
              ]),
              h('div', { style: 'color: #555;' }, [
                h('b', { style: 'color: #f59e0b;' }, '3️⃣ 친구의 친구'),
                h('span', { style: 'color: #666;' }, ': 2차 추천으로 참여 (referrerId와 referrerId2 모두 있음)')
              ])
            ])
          }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        dataIndex: 'referralSource',
        key: 'referralSource',
        width: 120
      },
      {
        title: '병원',
        dataIndex: 'hospital',
        key: 'hospital',
        width: 150,
        ellipsis: true
      },
      {
        title: () => h('span', [
          '과거 이벤트 참여이력',
          h(Tooltip, { title: '이전에 참여한 이벤트 목록' }, () => 
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        key: 'eventMetadata',
        width: 300
      },
      {
        title: '참여 단계',
        key: 'progress',
        width: 200,
        align: 'center'
      },
      {
        title: () => h('span', [
          '마수동',
          h(Tooltip, { title: '마케팅수신동의' }, () => 
            h('i', { class: 'fa fa-question-circle help-icon', style: { marginLeft: '4px', color: '#999', fontSize: '12px' } })
          )
        ]),
        key: 'marketingConsent',
        width: 80,
        align: 'center'
      },
      {
        title: '현재 상태',
        key: 'status',
        width: 100,
        align: 'center'
      },
      {
        title: '마지막 활동',
        dataIndex: 'lastActivity',
        key: 'lastActivity',
        width: 150
      },
      {
        title: '액션',
        key: 'actions',
        width: 100,
        fixed: 'right',
        align: 'center'
      }
    ];

    // 참여자 통계
    const stats = computed(() => {
      return {
        total: props.participants.length
      };
    });
    
    // 활성 필터 개수 계산
    // 동기화 버튼 텍스트
    const syncButtonText = computed(() => {
      switch(syncStatus.value) {
        case 'processing':
          return `동기화 중... (${syncProgress.value.progressPercentage || 0}%)`;
        case 'completed':
          return '재동기화';
        case 'failed':
          return '재시도';
        default:
          return '인증서 동기화';
      }
    });

    // 세그먼트 동기화 버튼 텍스트
    const segmentSyncButtonText = computed(() => {
      switch(segmentSyncStatus.value) {
        case 'processing':
          return `동기화 중... (${segmentSyncProgress.value.percentage || 0}%)`;
        case 'completed':
          return '재동기화';
        case 'failed':
          return '재시도';
        default:
          return '고객 세그멘테이션';
      }
    });

    const activeFilterCount = computed(() => {
      let count = 0;
      if (selectedEvent.value && selectedEvent.value !== '') count++;
      if (selectedActivityTypes.value.length > 0) count++;
      if (searchKeyword.value) count++;
      if (selectedReferralSource.value) count++;
      if (selectedEventMetadataFilter.value) count++;
      if (selectedCouponInfo.value) count++;
      if (selectedCouponNumber.value) count++;
      if (minCouponCount.value && minCouponCount.value > 0) count++;
      if (selectedReviewFilter.value) count++;
      if (selectedReviewContentFilter.value) count++;
      if (selectedMarketingConsent.value) count++;
      if (selectedDateRange.value && selectedDateRange.value.length === 2) count++;
      return count;
    });

    // 필터링된 데이터 (API에서 가져온 데이터 사용)
    const filteredData = computed(() => {
      // participantData가 배열인지 확인
      if (!Array.isArray(participantData.value)) {
        console.warn('participantData is not an array:', participantData.value);
        return [];
      }
      return participantData.value.map(item => ({ ...item, key: item.id || item._id }));
    });

    // 메서드
    const handleFilterChange = (value) => {
      emit('filter-change', value);
      selectedRowKeys.value = [];
      selectedRows.value = [];
    };

    const handleSearch = () => {
      console.log('Searching:', searchKeyword.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const handleRefresh = () => {
      loadEvents();
      loadParticipants();
      message.success('데이터를 새로고침했습니다.');
    };
    
    const handleReviewFilterChange = () => {
      console.log('Review filter changed:', selectedReviewFilter.value);
      
      // 리뷰 유무가 "있음"이 아닌 경우 리뷰 내용 유무 필터 초기화
      if (selectedReviewFilter.value !== 'exists') {
        selectedReviewContentFilter.value = null;
      }
      
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };
    
    const handleReviewContentFilterChange = () => {
      console.log('Review content filter changed:', selectedReviewContentFilter.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };
    
    const handleMarketingConsentChange = () => {
      console.log('Marketing consent filter changed:', selectedMarketingConsent.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const onSelectChange = (keys, rows) => {
      selectedRowKeys.value = keys;
      selectedRows.value = rows;
    };

    const handleSendMessage = () => {
      messageRecipients.value = selectedRows.value;
      messageDrawerVisible.value = true;
    };

    const sendIndividualMessage = (record) => {
      messageRecipients.value = [record];
      messageDrawerVisible.value = true;
    };
    
    const handleMessageSent = (data) => {
      console.log('Message sent:', data);
      emit('send-message', data);
    };

    const viewDetail = (record) => {
      console.log('View detail:', record);
      selectedParticipant.value = record;
      detailDrawerVisible.value = true;
    };

    // 참여정보 초기화 함수
    const resetParticipationInfo = async (record) => {
      const customerName = record.customerName || record.customerInfo?.name || '참여자';
      const customerMobile = record.customerMobile || record.customerInfo?.mobile || '연락처 없음';

      const confirmText = `정말로 ${customerName}님(${customerMobile})의 참여정보를 초기화하시겠습니까? 이 작업은 되돌릴 수 없습니다.`;

      const isConfirmed = await twoBtnDialog(
        '참여정보 초기화',
        confirmText,
        '초기화'
      );

      if (isConfirmed) {
        // API 호출
        const customerId = record.customerId;
        const eventId = selectedEvent.value;

        if (!customerId) {
          message.error('고객 ID가 없어 초기화할 수 없습니다.');
          return;
        }

        if (!eventId) {
          message.error('이벤트를 선택해주세요.');
          return;
        }

        message.loading('참여정보를 초기화하는 중...', 0);

        if (apiMethods && apiMethods.resetParticipationInfo) {
          apiMethods.resetParticipationInfo(
            eventId,
            customerId,
            (response) => {
              message.destroy();
              message.success(`${customerName}님의 참여정보가 초기화되었습니다.`);

              // 목록 새로고침
              loadParticipants();

              // 선택된 항목에서 제거
              const index = selectedRows.value.findIndex(r => r._id === record._id);
              if (index > -1) {
                selectedRows.value.splice(index, 1);
                selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== record._id);
              }
            },
            (error) => {
              message.destroy();
              const errorMessage = error.response?.data?.message || '참여정보 초기화에 실패했습니다.';
              message.error(errorMessage);
              console.error('참여정보 초기화 실패:', error);
            }
          );
        } else {
          message.destroy();
          message.error('API 초기화 오류가 발생했습니다.');
        }
      } else {
        console.log('참여정보 초기화 취소');
      }
    };
    
    // 펼쳐보기/접기 토글 함수
    const toggleEventExpand = (recordId) => {
      expandedEvents.value[recordId] = !expandedEvents.value[recordId];
    };

    // 표시할 이벤트 목록 반환 함수
    const getDisplayedEvents = (record) => {
      if (expandedEvents.value[record._id]) {
        return record.eventMetadata;
      }
      return record.eventMetadata.slice(0, 2);
    };

    // 개별 이벤트 클릭 핸들러 - 특정 과거 이벤트로 필터링
    const handleSingleEventClick = (record, event) => {
      const eventId = event.eventId;
      
      // 이벤트 리스트에서 해당 이벤트가 있는지 확인
      const eventExists = eventList.value.some(e => e._id === eventId);
      
      if (!eventExists) {
        // 이벤트가 리스트에 없으면 모든 이벤트를 다시 로드
        message.info('과거 이벤트 데이터를 불러오는 중...');
        
        // 모든 상태의 이벤트를 다시 로드 (과거 이벤트 포함)
        getEventsWithStatus(
          { limit: 200 }, // status 필터 없이 모든 이벤트 조회
          (response) => {
            if (response.data) {
              const events = response.data.items || response.data.data || response.data || [];
              eventList.value = Array.isArray(events) ? events : [];
              
              // 과거 이벤트 선택
              selectedEvent.value = eventId;
              // 검색어 필터에 참여자 연락처 설정
              searchKeyword.value = record.customerMobile;
              
              // 필터 적용을 위해 데이터 재조회
              pagination.value.current = 1;
              loadParticipants();
              
              message.success(`'${record.customerName}' 참여자의 이벤트 '${event.eventTitle}'로 필터링되었습니다.`);
            }
          },
          (error) => {
            console.error('이벤트 목록 로드 실패:', error);
            message.error('과거 이벤트를 불러오는 중 오류가 발생했습니다.');
          }
        );
      } else {
        // 이벤트가 이미 리스트에 있으면 바로 선택
        selectedEvent.value = eventId;
        // 검색어 필터에 참여자 연락처 설정
        searchKeyword.value = record.customerMobile;
        
        // 필터 적용을 위해 데이터 재조회
        pagination.value.current = 1;
        loadParticipants();
        
        message.success(`'${record.customerName}' 참여자의 이벤트 '${event.eventTitle}'로 필터링되었습니다.`);
      }
    };
    
    // 고객 아이디 복사
    const copyCustomerId = (record) => {
      if (record.customerId) {
        navigator.clipboard.writeText(record.customerId).then(() => {
          message.success(`고객 아이디가 복사되었습니다: ${record.customerId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('고객 아이디가 없습니다.');
      }
    };
    
    // 이벤트 참여자 아이디 복사
    const copyEventAttendeesId = (record) => {
      const attendeeId = record._id || record.id;
      if (attendeeId) {
        navigator.clipboard.writeText(attendeeId).then(() => {
          message.success(`이벤트 참여자 아이디가 복사되었습니다: ${attendeeId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('이벤트 참여자 아이디가 없습니다.');
      }
    };

    // URL 도메인 설정 함수 - 현재 도메인의 프리픽스를 기준으로 설정
    const getCustomerDomain = () => {
      const currentDomain = window.location.hostname;

      // 현재 도메인에서 프리픽스 추출
      if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
        return 'https://local-customer.certi.life';
      } else if (currentDomain.startsWith('operation.certi.life')) {
        // 운영 환경 (프리픽스 없음)
        return 'https://customer.certi.life';
      } else if (currentDomain.startsWith('stg-')) {
        // 스테이징 환경
        return 'https://intest-customer.certi.life';
      } else if (currentDomain.startsWith('dev-')) {
        // 개발 환경
        return 'https://dev-customer.certi.life';
      } else if (currentDomain.startsWith('intest-')) {
        // intest 환경
        return 'https://intest-customer.certi.life';
      } else {
        // 기본값: 운영
        return 'https://customer.certi.life';
      }
    };

    // 환자 링크 복사 함수
    const copyPatientLink = (record) => {
      const attendeeId = record._id || record.id;
      if (!selectedEvent.value) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }
      if (!attendeeId) {
        message.warning('이벤트 참여자 아이디가 없습니다.');
        return;
      }

      const domain = getCustomerDomain();
      const patientUrl = `${domain}/event-v2/patient/certilife-zeiss-2025-fall?tenant=certilife&snsType=kakao&locale=ko&eventId=${selectedEvent.value}&eventAttendeeId=${attendeeId}`;

      navigator.clipboard.writeText(patientUrl).then(() => {
        message.success('환자 링크가 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };

    // 친구 링크 복사 함수
    const copyFriendLink = (record) => {
      const attendeeId = record._id || record.id;
      if (!selectedEvent.value) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }
      if (!attendeeId) {
        message.warning('이벤트 참여자 아이디가 없습니다.');
        return;
      }

      const domain = getCustomerDomain();
      const friendUrl = `${domain}/event-v2/friend/certilife-zeiss-2025-fall?tenant=certilife&snsType=kakao&locale=ko&eventId=${selectedEvent.value}&referrerId=${attendeeId}&shareSource=direct`;

      navigator.clipboard.writeText(friendUrl).then(() => {
        message.success('친구 링크가 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };

    const getStatusColor = (status) => {
      const colors = {
        requested: 'default',
        participated: 'processing',
        shared: 'blue',
        coupon_pending: 'orange',
        coupon_received: 'green',
        logged_in: 'purple'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        requested: '미참여',
        participated: '참여',
        shared: '공유 완료',
        coupon_pending: '쿠폰 수령 대기',
        coupon_received: '쿠폰 수령 완료',
        logged_in: '로그인 완료'
      };
      return texts[status] || status;
    };
    
    const getEventStatusText = (status) => {
      const statusTexts = {
        draft: '임시저장',
        ongoing: '진행중',
        ended: '종료',
        scheduled: '예정'
      };
      return statusTexts[status] || status;
    };
    
    const getReferralSource = (record) => {
      // referrerId와 referrerId2 값으로 유입 경로 판단
      const hasReferrerId = record.referrerId && record.referrerId !== '';
      const hasReferrerId2 = record.referrerId2 && record.referrerId2 !== '';

      if (!hasReferrerId && !hasReferrerId2) {
        return '인증서 수신자';
      } else if (hasReferrerId && !hasReferrerId2) {
        return '환자의 친구';
      } else if (hasReferrerId && hasReferrerId2) {
        return '친구의 친구';
      }

      return '-';
    };
    
    // 고객분류 라벨 반환
    const getCustomerTypeLabel = (type) => {
      const labels = {
        // THERMAGE 관련 항목 주석 처리 (추후 노출 예정)
        // 'THERMAGE_WITH_ZEISS': '써마지+자이스',
        // 'THERMAGE_ONLY': '써마지만',
        'EXISTING_CERTIFICATE': '기존인증서',
        'NEW_CERTIFICATE': '신규인증서',
        'NON_CERTIFICATE': '인증서미발급',
        'NOT_PARTICIPATED': '미참여'
      };
      return labels[type] || type;
    };

    // 고객분류 색상 반환
    const getCustomerTypeColor = (type) => {
      const colors = {
        // THERMAGE 관련 항목 주석 처리 (추후 노출 예정)
        // 'THERMAGE_WITH_ZEISS': 'purple',  // 프리미엄 고객
        // 'THERMAGE_ONLY': 'orange',        // 써마지만
        'EXISTING_CERTIFICATE': 'blue',    // 기존 고객
        'NEW_CERTIFICATE': 'green',        // 신규 고객
        'NON_CERTIFICATE': 'default',      // 잠재 고객
        'NOT_PARTICIPATED': 'red'          // 비활성
      };
      return colors[type] || 'default';
    };

    const getReferralSourceColor = (record) => {
      // referrerId와 referrerId2 값으로 유입 경로의 색상 결정
      const hasReferrerId = record.referrerId && record.referrerId !== '';
      const hasReferrerId2 = record.referrerId2 && record.referrerId2 !== '';

      if (!hasReferrerId && !hasReferrerId2) {
        return 'purple'; // 인증서 수신자 - 보라색
      } else if (hasReferrerId && !hasReferrerId2) {
        return 'blue'; // 환자의 친구 - 파란색
      } else if (hasReferrerId && hasReferrerId2) {
        return 'green'; // 친구의 친구 - 초록색
      }

      return 'default';
    };
    
    // API 호출 메서드들
    const loadEvents = () => {
      eventsLoading.value = true;

      getEventsWithStatus(
        { status: 'ongoing', limit: 100 },
        (response) => {
          if (response.data) {
            // API 응답 구조에 따라 데이터 추출 (items 배열 사용)
            const events = response.data.items || response.data.data || response.data || [];
            // 이벤트 목록을 eventTitle과 eventStatus를 포함하여 저장
            eventList.value = Array.isArray(events) ? events : [];

            // 고정 옵션 추가 - 써마지쿠폰수령
            const thermageOption = {
              _id: 'THERMAGE',
              eventTitle: '써마지쿠폰수령',
              eventStatus: 'ongoing'
            };
            eventList.value.push(thermageOption);

            console.log('이벤트 목록 로드 완료:', eventList.value.length, '개');
            
            // 이벤트가 선택되지 않은 경우에만 첫 번째 이벤트 자동 선택
            if (eventList.value.length > 0 && !selectedEvent.value) {
              const firstEventId = eventList.value[0]._id;
              selectedEvent.value = firstEventId;
              emit('event-change', firstEventId);
              console.log('첫 번째 이벤트 자동 선택:', eventList.value[0].eventTitle);
              
              // 첫 번째 이벤트의 참여자 로드 (액티비티 타입 포함)
              loadParticipants();
            }
          }
          eventsLoading.value = false;
        },
        (error) => {
          console.error('이벤트 목록 로드 실패:', error);
          message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
          eventsLoading.value = false;
        }
      );
    };
    
    const loadParticipants = () => {
      // 이벤트가 선택되지 않은 경우 참여자 데이터를 로드하지 않음
      if (!selectedEvent.value || selectedEvent.value === '') {
        participantData.value = [];
        pagination.value.total = 0;
        console.log('이벤트가 선택되지 않아 참여자 데이터를 표시하지 않습니다.');
        return Promise.resolve();
      }
      
      const params = {
        page: pagination.value.current,
        limit: pagination.value.pageSize,
        showGlobalLoading: false
      };
      
      // 활동 유형 필터
      if (selectedActivityTypes.value.length > 0) {
        params.activityTypes = selectedActivityTypes.value;
      }
      
      // 검색어 필터
      if (searchKeyword.value) {
        params.search = searchKeyword.value;
      }
      
      // 유입 경로 필터 추가
      if (selectedReferralSource.value) {
        if (selectedReferralSource.value === 'certificate') {
          // 인증서 수신자: referrerId와 referrerId2 모두 없음
          params.referrerIdFilter = 'notExists';
          params.referrerId2Filter = 'notExists';
        } else if (selectedReferralSource.value === 'patient_friend') {
          // 환자의 친구: referrerId가 있고 referrerId2 없음
          params.referrerIdFilter = 'exists';
          params.referrerId2Filter = 'notExists';
        } else if (selectedReferralSource.value === 'friend_of_friend') {
          // 친구의 친구: referrerId와 referrerId2 모두 있음
          params.referrerIdFilter = 'exists';
          params.referrerId2Filter = 'exists';
        }
      }
      
      // 과거 이벤트 참여 이력 필터 추가
      if (selectedEventMetadataFilter.value) {
        params.eventMetadataFilter = selectedEventMetadataFilter.value;
      }
      
      // 쿠폰 필터 추가
      if (selectedCouponInfo.value) {
        params.couponInfoFilter = selectedCouponInfo.value;
      }
      
      if (selectedCouponNumber.value) {
        params.couponNumberFilter = selectedCouponNumber.value;
      }
      
      // 최소 쿠폰 수 필터 추가
      if (minCouponCount.value && minCouponCount.value > 0) {
        params.minCouponCount = minCouponCount.value;
      }
      
      // 리뷰 필터 추가
      if (selectedReviewFilter.value) {
        params.reviewFilter = selectedReviewFilter.value;
      }
      
      // 리뷰 컨텐츠 필터 추가
      if (selectedReviewContentFilter.value) {
        params.reviewContentFilter = selectedReviewContentFilter.value;
      }
      
      // 마케팅 수신 동의 필터 추가
      if (selectedMarketingConsent.value) {
        params.marketingConsentFilter = selectedMarketingConsent.value;
      }
      
      // 날짜 필터 추가
      if (selectedDateType.value && selectedDateRange.value && selectedDateRange.value.length === 2) {
        const [startDate, endDate] = selectedDateRange.value;
        const formattedStart = dayjs(startDate).format('YYYY-MM-DD');
        const formattedEnd = dayjs(endDate).format('YYYY-MM-DD');
        
        switch (selectedDateType.value) {
          case 'participatedAt':
            params.participatedAtFrom = formattedStart;
            params.participatedAtTo = formattedEnd;
            break;
          case 'surgeryDate':
            params.surgeryDateFrom = formattedStart;
            params.surgeryDateTo = formattedEnd;
            break;
          case 'couponCreatedAt':
            params.couponCreatedAtFrom = formattedStart;
            params.couponCreatedAtTo = formattedEnd;
            break;
          case 'couponIssuedAt':
            params.couponIssuedAtFrom = formattedStart;
            params.couponIssuedAtTo = formattedEnd;
            break;
          case 'couponExpiryDate':
            params.couponExpiryDateFrom = formattedStart;
            params.couponExpiryDateTo = formattedEnd;
            break;
        }
      }
      
      const eventId = selectedEvent.value;
      
      return new Promise((resolve, reject) => {
        getEventAttendeesWithActivities(
          eventId,
          params,
          (response) => {
          console.log('참여자 데이터 API 응답:', response.data);
          if (response.data && response.data.data) {
            // API 응답 구조에 따라 데이터 추출 - response.data.data.items
            const responseData = response.data.data;
            const items = responseData.items || [];
            const paginationInfo = responseData.pagination || {};
            const activityTypesObj = responseData.activityTypes || {}; // 활동 유형 객체 추출
            const activityTypesData = activityTypesObj.activityTypes || []; // 실제 배열 추출
            
            // 활동 유형 데이터 처리
            if (Array.isArray(activityTypesData) && activityTypesData.length > 0) {
              // 카운트 기준 내림차순 정렬 (이미 정렬되어 있을 수 있지만 확실히 하기 위해)
              const sortedData = [...activityTypesData].sort((a, b) => b.count - a.count);
              availableActivityTypes.value = sortedData.map(item => ({
                type: item.type,
                label: `${item.type} (${item.count.toLocaleString()})`,
                count: item.count || 0
              }));
              console.log('활동 유형 데이터 로드 완료:', availableActivityTypes.value.length, '개');
            } else {
              console.log('활동 유형 데이터가 없거나 형식이 맞지 않습니다:', activityTypesObj);
              availableActivityTypes.value = [];
            }
            
            // 각 참여자 데이터를 테이블에 표시할 형식으로 변환
            if (Array.isArray(items)) {
              participantData.value = items.map(item => ({
                ...item,
                key: item._id,
                id: item._id,
                name: item.customerName || '',
                phone: item.customerMobile || '',
                // 이름 마스킹 처리
                customerName: maskName(item.customerName),
                // 전화번호 포맷팅 및 마스킹 처리
                customerMobile: formatAndMaskPhone(item.customerMobile),
                customerInfo: {
                  ...item.customerInfo,
                  name: maskName(item.customerInfo?.name),
                  mobile: formatAndMaskPhone(item.customerInfo?.mobile)
                },
                hospital: item.customerInfo?.companyMetadatas?.[0]?.name || '-',
                // 활동 기반 상태 계산
                sharedAt: item.activities?.find(a => a.type === '카카오톡 공유하기')?.timestamp || null,
                shareCount: item.shareCount || 0,  // API에서 직접 제공하는 shareCount 사용
                // 쿠폰 수령 여부: couponNumber가 있어야 실제 수령
                couponReceived: item.couponInfo?.some(c => c.couponNumber) || false,
                couponInfo: item.couponInfo || [],
                couponReceivedAt: item.couponInfo?.find(c => c.couponNumber)?.couponIssuedAt || null,
                loggedIn: false, // API 응답에 로그인 정보가 없으므로 false로 설정
                lastActivity: item.updatedAt || item.createdAt,
                status: determineStatus(item),
                marketingConsent: item.marketingConsent?.isAgreed ? 'agreed' : (item.marketingConsent?.isAgreed === false ? 'revoked' : null) // 마케팅 수신 동의 정보 추가
              }));
              
              // 페이지네이션 정보 업데이트
              pagination.value.total = paginationInfo.total || items.length;
              pagination.value.current = paginationInfo.currentPage || 1;
              
              console.log('참여자 데이터 로드 완료:', participantData.value.length, '명 (전체:', pagination.value.total, '명)');
              resolve();
            } else {
              console.error('items가 배열이 아닙니다:', items);
              participantData.value = [];
              pagination.value.total = 0;
              resolve();
            }
          } else {
            console.error('올바르지 않은 API 응답 구조:', response.data);
            participantData.value = [];
            pagination.value.total = 0;
            resolve();
          }
        },
        (error) => {
          console.error('참여자 데이터 로드 실패:', error);
          message.error('참여자 데이터를 불러오는 중 오류가 발생했습니다.');
          participantData.value = [];
          pagination.value.total = 0;
          reject(error);
        }
      );
      });
    };
    
    // 새로운 필터 메서드들
    const handleEventChange = (value) => {
      selectedEvent.value = value;
      selectedActivityTypes.value = []; // 이벤트 변경 시 활동 유형 초기화
      pagination.value.current = 1; // 페이지를 1로 리셋
      
      emit('event-change', value);
      console.log('Selected event:', value);
      
      // 참여자 데이터 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleActivityTypeChange = (values) => {
      selectedActivityTypes.value = values;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected activity types:', values);
      
      // 참여자 데이터 다시 로드
      loadParticipants();
    };
    
    const handleReferralSourceChange = (value) => {
      selectedReferralSource.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected referral source:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleCouponInfoChange = (value) => {
      selectedCouponInfo.value = value;
      
      // 쿠폰 발급 권한이 "권한 있음"이 아닌 경우 쿠폰 수령 여부 필터 초기화
      if (value !== 'exists') {
        selectedCouponNumber.value = null;
      }
      
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected coupon info filter:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleCouponNumberChange = (value) => {
      selectedCouponNumber.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected coupon number filter:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleMinCouponCountChange = (value) => {
      minCouponCount.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected min coupon count:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleEventMetadataFilterChange = (value) => {
      selectedEventMetadataFilter.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected event metadata filter:', value);
      
      // 참여자 데이터 다시 로드
      loadParticipants();
    };
    
    const handleDateTypeChange = (value) => {
      selectedDateType.value = value;
      // 날짜 유형이 변경되면 날짜 범위는 유지하고 API 호출
      console.log('Selected date type:', value);
      
      if (selectedDateRange.value && selectedDateRange.value.length === 2) {
        pagination.value.current = 1; // 페이지를 1로 리셋
        
        // 참여자 데이터 다시 로드 (활동 유형 포함)
        loadParticipants();
      }
    };
    
    const handleDateRangeChange = (dates) => {
      selectedDateRange.value = dates;
      
      if (dates && dates.length === 2) {
        pagination.value.current = 1; // 페이지를 1로 리셋
        console.log('Selected date range:', dates.map(d => dayjs(d).format('YYYY-MM-DD')));
        
        // 참여자 데이터 다시 로드 (활동 유형 포함)
        loadParticipants();
      } else if (!dates) {
        // 날짜 범위가 clear되면 데이터 다시 로드
        pagination.value.current = 1; // 페이지를 1로 리셋
        loadParticipants();
      }
    };
    
    const handleTableChange = (paginationInfo) => {
      pagination.value = { ...pagination.value, ...paginationInfo };
      loadParticipants();
    };
    
    const resetAllFilters = () => {
      selectedEvent.value = null;
      selectedActivityTypes.value = [];
      searchKeyword.value = '';
      selectedReferralSource.value = null;
      selectedEventMetadataFilter.value = null;
      selectedCouponInfo.value = null;
      selectedCouponNumber.value = null;
      minCouponCount.value = null;
      selectedReviewFilter.value = null;
      selectedReviewContentFilter.value = null;
      selectedMarketingConsent.value = null;
      selectedDateType.value = 'participatedAt'; // 기본값으로 리셋
      selectedDateRange.value = null;
      selectedRowKeys.value = [];
      selectedRows.value = [];
      
      // 데이터 초기화
      availableActivityTypes.value = [];
      participantData.value = [];
      pagination.value.total = 0;
      
      message.success('모든 필터가 초기화되었습니다.');
    };
    
    // 이벤트 참여 여부를 판단하는 통합 헬퍼 함수
    const hasParticipated = (record) => {
      return record.activities && Array.isArray(record.activities) && record.activities.length > 0;
    };
    
    // 참여자 상태를 결정하는 헬퍼 함수
    const determineStatus = (item) => {
      // activities가 없으면 미참여 상태
      if (!hasParticipated(item)) {
        return 'requested';
      }
      
      // 쿠폰번호가 있으면 실제 쿠폰 수령 상태
      if (item.couponInfo?.some(c => c.couponNumber)) {
        return 'coupon_received';
      }
      
      // 쿠폰 정보는 있지만 쿠폰번호가 없으면 당첨 대기 상태
      if (item.couponInfo && item.couponInfo.length > 0) {
        return 'coupon_pending';
      }
      
      // 카카오톡 공유 활동이 있으면 공유 완료 상태
      const hasShared = item.activities?.some(a => a.type === '카카오톡 공유하기');
      if (hasShared) {
        return 'shared';
      }
      
      // activities가 있으면 참여 상태
      return 'participated';
    };
    
    // 날짜 포맷 함수
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD HH:mm');
    };

    // 경과 시간 포맷팅 함수
    const formatElapsedTime = (startTime) => {
      if (!startTime) return '';
      const elapsed = Date.now() - startTime;
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}분 ${remainingSeconds}초`;
    };
    
    // 당첨알림 발송 핸들러
    const handleSendWinnerNotification = () => {
      // 선택된 참여자 확인
      if (selectedRows.value.length === 0) {
        message.warning('당첨알림을 발송할 참여자를 선택해주세요.');
        return;
      }
      
      // 이벤트 선택 확인
      if (!selectedEvent.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }
      
      // 참여자 ID 추출
      const attendeeIds = selectedRows.value.map(row => row._id || row.id);
      
      // API 호출
      if (apiMethods && apiMethods.sendWinnerNotifications) {
        apiMethods.sendWinnerNotifications(
          selectedEvent.value,
          {
            messageType: 'ALIM_TALK',
            attendeeIds: attendeeIds
          },
          (response) => {
            // API 응답 구조 분석
            const responseData = response.data?.data || response.data;
            const results = responseData.results || [];
            const successCount = responseData.successCount || 0;
            const failedCount = responseData.failedCount || 0;
            
            // 성공/실패 케이스별 메시지 생성
            if (successCount > 0 && failedCount === 0) {
              // 모두 성공
              message.success(`${successCount}명에게 당첨알림이 발송되었습니다.`);
            } else if (successCount === 0 && failedCount > 0) {
              // 모두 실패
              const failReasons = results.map(r => `${r.customerName}: ${r.reason}`).join('\n');
              message.warning(`당첨알림 발송 실패\n${failReasons}`);
            } else if (successCount > 0 && failedCount > 0) {
              // 일부 성공, 일부 실패
              const failedResults = results.filter(r => r.result === 'failed');
              const failReasons = failedResults.map(r => `${r.customerName}: ${r.reason}`).join(', ');
              message.info(`${successCount}명 발송 성공, ${failedCount}명 실패\n실패 사유: ${failReasons}`);
            } else {
              // 처리된 건이 없는 경우
              const summary = responseData.summary;
              message.warning(summary || '처리 가능한 참석자가 없습니다');
            }
            
            // 선택 초기화 및 목록 새로고침
            if (successCount > 0) {
              selectedRowKeys.value = [];
              selectedRows.value = [];
              loadParticipants();
            }
          },
          (error) => {
            message.error('당첨알림 발송 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };

    // 쿠폰 삭제 후 처리
    const handleCouponDeleted = async (coupon) => {
      console.log('쿠폰 삭제됨:', coupon);
      console.log('현재 선택된 이벤트:', selectedEvent.value);
      console.log('참여자 목록 새로고침 시작...');
      
      // 참여자 목록 새로고침
      await loadParticipants();
      
      // 드로워가 열려있는 참여자 데이터 업데이트
      if (selectedParticipant.value) {
        // 새로고침된 데이터에서 해당 참여자 찾기
        const updatedParticipant = participantData.value.find(
          p => (p._id || p.id) === (selectedParticipant.value._id || selectedParticipant.value.id)
        );
        if (updatedParticipant) {
          selectedParticipant.value = { ...updatedParticipant };
          console.log('선택된 참여자 데이터 업데이트됨');
        }
      }
      
      console.log('참여자 목록 새로고침 완료, 총', participantData.value.length, '명');
    };
    
    // 쿠폰 재발송 후 처리
    const handleCouponResent = (coupon) => {
      console.log('쿠폰 재발송됨:', coupon);
      // 참여자 목록 새로고침
      loadParticipants();
    };
    
    // 컴포넌트 마운트 시 초기 데이터 로드
    onMounted(() => {
      // 이벤트 목록 먼저 로드
      loadEvents();

      // props로 받은 selectedEventId가 있으면 자동으로 참여자 조회
      if (props.selectedEventId && props.selectedEventId !== '') {
        console.log('초기 로드: eventId로 참여자 조회 시작:', props.selectedEventId);
        selectedEvent.value = props.selectedEventId;
        // 이벤트 목록 로드 완료 후 참여자 로드를 위해 약간의 지연
        setTimeout(() => {
          loadParticipants();
        }, 500);
      }
    });

    // 컴포넌트 언마운트 시 정리
    onUnmounted(() => {
      // 폴링 중지
      stopPolling();
      stopSegmentPolling();
    });

    // 이벤트 수정 페이지로 이동
    const goToEditEvent = () => {
      // 현재 페이지 정보를 포함하여 이벤트 수정 페이지로 이동
      router.push({
        path: `/events/${selectedEvent.value}/edit`,
        query: {
          from: 'participation',
          returnUrl: router.currentRoute.value.fullPath
        }
      });
    };

    // 인증서 동기화 처리
    const handleCertificateSync = async () => {
      if (!selectedEvent.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      // 대량 처리 경고
      const totalCount = filteredData.value.length;
      if (totalCount > 1000) {
        const confirmResult = await new Promise((resolve) => {
          Modal.confirm({
            title: '대량 동기화 안내',
            content: `${totalCount.toLocaleString()}명의 참여자 인증서를 동기화합니다. 5-10분 소요될 수 있습니다. 계속하시겠습니까?`,
            okText: '동기화 시작',
            cancelText: '취소',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (!confirmResult) return;
      }

      // 동기화 시작 API 호출
      startCertificateSync(
        selectedEvent.value,
        (response) => {
          const data = response.data?.data || response.data;

          syncJobId.value = data.jobId;
          syncStatus.value = 'processing';
          syncProgress.value = {
            totalAttendees: data.totalAttendees || 0,
            processedCount: 0,
            skippedCount: 0,
            progressPercentage: 0,
            currentPhase: data.status || 'queued'
          };

          message.info(`${data.totalAttendees}명의 인증서 동기화가 시작되었습니다.`);

          // 폴링 시작
          startPolling();
        },
        (error) => {
          console.error('동기화 시작 실패:', error);
          message.error('인증서 동기화 시작에 실패했습니다: ' + (error.response?.data?.message || '알 수 없는 오류'));
        }
      );
    };

    // 폴링 시작
    const startPolling = () => {
      // 기존 폴링 정리
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
      }

      // 폴링 카운트 및 시작 시간 초기화
      pollingCount.value = 0;
      pollingStartTime.value = Date.now();
      syncElapsedTime.value = formatElapsedTime(pollingStartTime.value);

      // 5초 간격으로 상태 조회
      pollingInterval.value = setInterval(() => {
        pollingCount.value++;
        // 경과 시간 업데이트
        syncElapsedTime.value = formatElapsedTime(pollingStartTime.value);

        // 최대 시도 횟수 또는 최대 시간 체크
        const elapsedTime = Date.now() - pollingStartTime.value;
        if (pollingCount.value >= MAX_POLLING_COUNT || elapsedTime >= MAX_POLLING_TIME) {
          stopPolling();
          syncStatus.value = 'failed';
          message.error('인증서 동기화 시간 초과 (10분 경과)');

          // 3초 후 초기화
          setTimeout(() => {
            syncStatus.value = 'idle';
            syncProgress.value = {
              totalAttendees: 0,
              processedCount: 0,
              skippedCount: 0,
              progressPercentage: 0,
              currentPhase: null
            };
          }, 3000);
          return;
        }

        getCertificateSyncStatus(
          selectedEvent.value,
          (response) => {
            const data = response.data?.data || response.data;

            // percentage 기반으로 진행 상황 업데이트
            const currentPercentage = data.percentage || 0;

            // 진행 상황 업데이트 (화면 깜빡임 없이 부드럽게)
            syncProgress.value = {
              totalAttendees: data.totalAttendees || syncProgress.value.totalAttendees,
              processedCount: data.attendeesWithCertificate || data.processedCount || 0,
              skippedCount: data.attendeesWithoutCertificate || data.skippedCount || 0,
              progressPercentage: currentPercentage,
              currentPhase: data.currentPhase || data.status
            };

            // percentage가 100이면 완료로 처리
            if (currentPercentage === 100) {
              stopPolling();
              syncStatus.value = 'completed';

              const successMessage = `인증서 동기화 완료: ${data.attendeesWithCertificate}명 보유, ${data.attendeesWithoutCertificate}명 미보유`;
              message.success(successMessage);

              // 테이블 새로고침
              loadParticipants();

              // 3초 후 배너 숨기기
              setTimeout(() => {
                syncStatus.value = 'idle';
                syncProgress.value = {
                  totalAttendees: 0,
                  processedCount: 0,
                  skippedCount: 0,
                  progressPercentage: 0,
                  currentPhase: null
                };
              }, 3000);
            } else if (data.status === 'failed') {
              stopPolling();
              syncStatus.value = 'failed';

              message.error(`동기화 실패: ${data.lastError || '알 수 없는 오류'}`);

              // 3초 후 초기화
              setTimeout(() => {
                syncStatus.value = 'idle';
              }, 3000);
            }
            // idle 상태에서도 percentage가 100이 아니면 계속 진행 중으로 처리
            else if (data.status === 'idle' && currentPercentage < 100) {
              // 계속 폴링 진행 (동기화 진행 중)
              console.log(`동기화 진행 중: ${currentPercentage}%`);
            }
          },
          (error) => {
            // 네트워크 에러는 무시하고 계속 폴링
            console.error('상태 조회 실패:', error);
          }
        );
      }, 5000); // 5초 간격
    };

    // 폴링 중지
    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
      // 폴링 관련 상태 초기화
      pollingCount.value = 0;
      pollingStartTime.value = null;
      syncElapsedTime.value = '';
    };

    // 동기화 단계 텍스트 변환
    const getCurrentPhaseText = (phase) => {
      const phases = {
        'QUEUED': '대기 중',
        'FETCHING_NFTS': 'NFT 조회 중',
        'PROCESSING': '처리 중'
      };
      return phases[phase] || phase || '준비 중';
    };

    // 세그먼트 동기화 처리
    const handleSegmentSync = async () => {
      if (!selectedEvent.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      // 대량 처리 경고
      const totalCount = filteredData.value.length;
      if (totalCount > 1000) {
        const confirmResult = await new Promise((resolve) => {
          Modal.confirm({
            title: '대량 동기화 안내',
            content: `${totalCount.toLocaleString()}명의 참여자 세그먼트를 동기화합니다. 5-10분 소요될 수 있습니다. 계속하시겠습니까?`,
            okText: '동기화 시작',
            cancelText: '취소',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmResult) return;
      }

      // 동기화 시작 API 호출
      segmentSyncStatus.value = 'processing';
      segmentSyncProgress.value = {
        remainingCount: 0,
        syncedCount: 0,
        totalCount: 0,
        percentage: 0
      };

      instance.proxy.$apiInstance.post(`/events-v2/segments/sync/event/${selectedEvent.value}`, {
        confirmed: true
      }, {
        showGlobalLoading: false  // 전역 로딩 비활성화
      }).then((response) => {
        const data = response.data?.data || response.data;
        message.info('세그먼트 동기화가 시작되었습니다.');

        // 폴링 시작
        startSegmentPolling();
      }).catch((error) => {
        console.error('세그먼트 동기화 시작 실패:', error);
        segmentSyncStatus.value = 'failed';
        message.error('세그먼트 동기화 시작에 실패했습니다: ' + (error.response?.data?.message || '알 수 없는 오류'));
      });
    };

    // 세그먼트 폴링 시작
    const startSegmentPolling = () => {
      // 기존 폴링 정리
      if (segmentPollingInterval.value) {
        clearInterval(segmentPollingInterval.value);
      }

      // 폴링 카운트 및 시작 시간 초기화
      segmentPollingCount.value = 0;
      segmentPollingStartTime.value = Date.now();
      segmentElapsedTime.value = formatElapsedTime(segmentPollingStartTime.value);

      // 5초 간격으로 상태 조회
      segmentPollingInterval.value = setInterval(() => {
        segmentPollingCount.value++;
        // 경과 시간 업데이트
        segmentElapsedTime.value = formatElapsedTime(segmentPollingStartTime.value);

        // 최대 시도 횟수 또는 최대 시간 체크
        const elapsedTime = Date.now() - segmentPollingStartTime.value;
        if (segmentPollingCount.value >= MAX_POLLING_COUNT || elapsedTime >= MAX_POLLING_TIME) {
          stopSegmentPolling();
          segmentSyncStatus.value = 'failed';
          message.error('세그먼트 동기화 시간 초과 (10분 경과)');

          // 3초 후 초기화
          setTimeout(() => {
            segmentSyncStatus.value = 'idle';
            segmentSyncProgress.value = {
              remainingCount: 0,
              syncedCount: 0,
              totalCount: 0,
              percentage: 0
            };
          }, 3000);
          return;
        }

        // 상태 조회 API 호출
        instance.proxy.$apiInstance.get(`/events-v2/segments/sync/event/${selectedEvent.value}/status`, {
          showGlobalLoading: false  // 전역 로딩 비활성화
        })
          .then((response) => {
            const data = response.data?.data || response.data;

            // 진행 상황 업데이트
            const syncedCount = data.syncedCount || 0;
            const totalCount = data.totalCount || 0;
            const remainingCount = data.remainingCount || 0;

            // 진행률 계산: syncedCount / totalCount
            const calculatedPercentage = totalCount > 0 ? Math.round((syncedCount / totalCount) * 100) : 0;

            segmentSyncProgress.value = {
              remainingCount: remainingCount,
              syncedCount: syncedCount,
              totalCount: totalCount,
              percentage: data.progress?.percentage || calculatedPercentage
            };

            // 완료 체크
            if (!data.hasPendingJob && data.needsSync === false) {
              stopSegmentPolling();
              segmentSyncStatus.value = 'completed';
              message.success(`세그먼트 동기화 완료: ${data.syncedCount}명 처리됨`);

              // 테이블 새로고침
              loadParticipants();

              // 3초 후 상태 초기화
              setTimeout(() => {
                segmentSyncStatus.value = 'idle';
                segmentSyncProgress.value = {
                  remainingCount: 0,
                  syncedCount: 0,
                  totalCount: 0,
                  percentage: 0
                };
              }, 3000);
            }
          })
          .catch((error) => {
            // 네트워크 에러는 무시하고 계속 폴링
            console.error('세그먼트 상태 조회 실패:', error);
          });
      }, 5000); // 5초 간격
    };

    // 세그먼트 폴링 중지
    const stopSegmentPolling = () => {
      if (segmentPollingInterval.value) {
        clearInterval(segmentPollingInterval.value);
        segmentPollingInterval.value = null;
      }
      // 폴링 관련 상태 초기화
      segmentPollingCount.value = 0;
      segmentPollingStartTime.value = null;
      segmentElapsedTime.value = '';
    };

    return {
      isFilterCollapsed,
      searchKeyword,
      selectedRowKeys,
      selectedRows,
      selectedEvent,
      selectedActivityTypes,
      selectedReferralSource,
      selectedCouponInfo,
      selectedCouponNumber,
      minCouponCount,
      selectedEventMetadataFilter,
      selectedReviewFilter,
      selectedReviewContentFilter,
      selectedMarketingConsent,
      selectedDateType,
      selectedDateRange,
      eventList,
      availableActivityTypes,
      eventsLoading,
      activeFilterCount,
      columns,
      stats,
      filteredData,
      pagination,
      detailDrawerVisible,
      selectedParticipant,
      messageDrawerVisible,
      messageRecipients,
      handleFilterChange,
      handleSearch,
      handleRefresh,
      onSelectChange,
      handleSendMessage,
      handleSendWinnerNotification,
      sendIndividualMessage,
      viewDetail,
      resetParticipationInfo,
      handleSingleEventClick,
      copyCustomerId,
      copyEventAttendeesId,
      copyPatientLink,
      copyFriendLink,
      handleCouponDeleted,
      handleCouponResent,
      hasParticipated,
      toggleEventExpand,
      getDisplayedEvents,
      expandedEvents,
      getStatusColor,
      getStatusText,
      getEventStatusText,
      getCustomerTypeLabel,
      getCustomerTypeColor,
      getReferralSource,
      getReferralSourceColor,
      handleEventChange,
      handleActivityTypeChange,
      handleReferralSourceChange,
      handleCouponInfoChange,
      handleCouponNumberChange,
      handleMinCouponCountChange,
      handleEventMetadataFilterChange,
      handleDateTypeChange,
      handleDateRangeChange,
      handleReviewFilterChange,
      handleReviewContentFilterChange,
      handleMarketingConsentChange,
      handleTableChange,
      resetAllFilters,
      formatDate,
      handleMessageSent,
      goToEditEvent,
      handleCertificateSync,
      syncStatus,
      syncProgress,
      syncButtonText,
      getCurrentPhaseText,
      handleSegmentSync,
      segmentSyncStatus,
      segmentSyncProgress,
      segmentSyncButtonText,
      segmentElapsedTime,
      syncElapsedTime
    };
  }
};
</script>

<style scoped>
.participant-monitoring {
  padding: 20px;
}

.filter-section {
  background: #fafafa;
  padding: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

/* 필터 헤더 */
.filter-header {
  padding-bottom: 0;
}

.filter-title {
  font-size: 16px;
  font-weight: 600;
  color: #595959;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-title i {
  color: #d4b106;
}

.collapse-button {
  color: #666;
}

.collapse-button:hover {
  color: #d4b106;
}

/* 필터 콘텐츠 */
.filter-content {
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  margin-top: 16px;
}

.filter-row {
  margin-bottom: 0;
}

.filter-label {
  display: block;
  font-size: 12px;
  color: #595959;
  margin-bottom: 6px;
  font-weight: 500;
}

.mt-3 {
  margin-top: 16px;
}

.table-header {
  padding: 0 8px;
}

/* 진행 상태 인디케이터 */
.progress-indicators {
  display: flex;
  align-items: center;
  gap: 4px;
}

.indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #999;
  font-size: 12px;
  transition: all 0.3s;
  position: relative;
}

.indicator.active {
  background: #d4b106;
  color: white;
}

.indicator-line {
  width: 20px;
  height: 2px;
  background: #f0f0f0;
  transition: all 0.3s;
}

.indicator-line.active {
  background: #d4b106;
}

/* 공유 횟수 표시 */
.share-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ff4d4f;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  min-width: 16px;
  text-align: center;
  line-height: 1;
}

/* 인증서 셀 스타일 */
.certificates-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.yellow-button {
  background-color: #FFD814;
  border-color: #FFD814;
}

.yellow-button:hover {
  background-color: #E5C112;
  border-color: #E5C112;
}

/* 인증서 동기화 진행 배너 스타일 */
.sync-progress-banner {
  margin-bottom: 20px;
}

.sync-banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.sync-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sync-stats {
  color: #595959;
}

.sync-time {
  margin-left: 8px;
  color: #8c8c8c;
  font-size: 14px;
  font-weight: normal;
}

.sync-details {
  padding-top: 8px;
}

.sync-details .ant-progress {
  margin-bottom: 12px;
}

.sync-details .ant-col {
  font-size: 14px;
  color: #595959;
}

/* 액티비티 체크박스 그룹 */
.activity-checkbox-group {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 6px;
}

.activity-label {
  font-size: 14px;
  display: inline-block;
  line-height: 1.5;
}

/* 체크박스 스타일 */
.activity-checkbox-group :deep(.ant-checkbox-wrapper) {
  display: flex;
  align-items: center;
  padding: 4px 0;
  width: 100%;
}

.activity-checkbox-group :deep(.ant-checkbox-wrapper):hover {
  color: #d4b106;
}

.activity-checkbox-group :deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #d4b106;
  border-color: #d4b106;
}

/* 필터 섹션 패딩 조정 */
.filter-section {
  background: #fafafa;
  padding: 24px;
  border-radius: 8px;
}

</style>

<style>
/* 커스텀 배지 스타일 */
.custom-badge-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #52c41a;
  color: #ffffff !important;
  font-size: 12px;
  font-weight: bold;
  padding: 0 6px;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 0 0 1px #ffffff;
}
</style>